import { useEffect, useState, useContext } from 'react';

import { axiosGet } from '../../../../utils/axiosGet';
import { axiosPost } from '../../../../utils/axiosPost';
import { CommonCollections } from '../../../../App';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { useAdminCollections } from '../../../../context/AdminCollections';
import { getIndependentAssessorFormData } from '../../../../utils/getIndependentAssessorFormData';
import { useLoggedInUserContextProvider } from '../../../../context/LoggedInUserContext';
import { formatIndependentAssessorData } from '../../../../utils/formatIndependentAssessorData';
import { useRefereeCollections } from '../../../../context/RefereeReportCollections';
import { ELECTION_TYPE } from '../../../../utils/constants';
import { devError } from '../../../../utils/devLog';
import { useWizardNotification } from './useWizardNotification';

const useAssessorWizard = () => {
  const { userDetails, originalUserDetails } = useLoggedInUserContextProvider();
  const {
    sectionalCommittees,
    activeFellowsArray,
    organisations,
    nonAasRefereesArray,
    addNonAasReferee,
    titlesTypes,
  } = useStaticCollections();
  const { candidates, nominationArray } = useAdminCollections();
  const { nominationId, setNominationId, refereeReportId } =
    useContext(CommonCollections);

  const { refereeReports, setNonAdminRefereeReportId, removeRefereeReport } =
    useRefereeCollections();

  const [formStatus, setFormStatus] = useState(1);
  const [refereeReportData, setRefereeReportData] = useState({});
  const [originalRefereeData, setOriginalRefereeData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [modalBodyText, setModalBodyText] = useState('');
  const [formName, setFormName] = useState(null);
  const { notification, showNotification } = useWizardNotification();
  const [submittedRefereeName, setSubmittedRefereeName] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleCancelSelect = () => {
    setShowModal(false);
  };

  const onSubmit = (data) => {
    const independentAssessorData = formatIndependentAssessorData(data, titlesTypes);
    if (
      parseInt(independentAssessorData[formName]) !== 1 &&
      parseInt(independentAssessorData[formName]) !== 0
    ) {
      delete independentAssessorData[formName];
    }
    const formattedData = {
      ...independentAssessorData,
      ...refereeReportData,
      nominationId,
    };

    formattedData.refereeIndex = formName;

    setFormData(formattedData);

    const getRefereeLabel = (refereeNumber) => {
      const isFellow = parseInt(formattedData[`referee${refereeNumber}`]) === 1;
      const fellowId = parseInt(
        formattedData[`referee${refereeNumber}FellowId`],
      );
      const nonAasId = parseInt(
        formattedData[`nonAasReferee${refereeNumber}Id`],
      );
      const newNonAas = formattedData[`newNonAasReferee${refereeNumber}`];

      if (isFellow) {
        if (
          originalRefereeData[`referee${refereeNumber}FellowId`]?.value !==
          fellowId
        ) {
          const fellowData = activeFellowsArray.find(
            (fellow) => fellow.id === fellowId,
          );
          return fellowData?.attributes?.label || '';
        }
        return '';
      }

      if (nonAasId) {
        if (
          originalRefereeData[`nonAasReferee${refereeNumber}Id`]?.value !==
          nonAasId
        ) {
          const nonAasData = nonAasRefereesArray.find(
            (ref) => ref.id === nonAasId,
          );
          return nonAasData?.attributes?.label || '';
        }
        return '';
      }

      return newNonAas?.label || '';
    };

    const refereeLabels = ['Seven', 'Eight', 'Nine'].map((refereeNumber) =>
      getRefereeLabel(refereeNumber),
    );

    const refereeLabel = refereeLabels.find((label) => label) || '';

    if (refereeLabel === '') {
      showNotification(
        'Cannot submit',
        'No referee is selected. Please choose an AAS fellow or non-AAS referee before submitting.',
      );
      return;
    }

    setSubmittedRefereeName(refereeLabel);

    const escapeHtml = (str) =>
      String(str).replace(
        /[&<>"']/g,
        (c) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          })[c],
      );

    setModalBodyText(
      `Please note the following independent assessor will be notified for references:
      </br>**${escapeHtml(refereeLabel)}**<br />`,
    );
    setShowModal(true);
  };

  const handleConfirmSelect = async () => {
    try {
      const response = await axiosPost('independent-assessor-post', {
        data: formData,
      });

      if (response.status === 200) {
        setShowModal(false);

        if (response.data?.newNonAasReferee) {
          addNonAasReferee(response.data.newNonAasReferee);
        }

        showNotification(
          'Independent Assessor Submitted',
          `${submittedRefereeName} has been successfully added as an Independent Assessor.`,
        );

        const newRefereeReportId = response.data?.id;

        if (newRefereeReportId) {
          if (refereeReportId && refereeReportId !== newRefereeReportId) {
            removeRefereeReport(refereeReportId);
          }

          setNonAdminRefereeReportId(newRefereeReportId);
        }

        setIsRedirecting(true);

        setTimeout(() => {
          setNominationId(null);
          setIsRedirecting(false);
        }, 3500);
      }
    } catch (error) {
      devError('Error submitting independent assessor form:', error);
      showNotification(
        'Error',
        'Failed to submit independent assessor. Please try again.',
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!nominationId) return;

      if (isRedirecting) return;

      setIsLoading(true);

      try {
        const { data: existingUsers } = await axiosGet(
          'independent-assessor-get-users',
          { nominationId },
        );

        const { data: nominationReferee } = await axiosGet(
          'get-independent-assessors',
          { data: nominationId },
        );

        let computedFormName = '';
        if (!refereeReportId) {
          if (
            nominationReferee.refereeSevenTypeId !== 0 &&
            nominationReferee.refereeSevenTypeId !== 1
          ) {
            computedFormName = 'refereeSeven';
          } else if (
            nominationReferee.refereeEightTypeId !== 0 &&
            nominationReferee.refereeEightTypeId !== 1
          ) {
            computedFormName = 'refereeEight';
          } else if (
            nominationReferee.refereeNineTypeId !== 0 &&
            nominationReferee.refereeNineTypeId !== 1
          ) {
            computedFormName = 'refereeNine';
          } else {
            devError(
              'WizardIndependentAssessor: slots 7/8/9 all assigned, defaulting computedFormName to refereeSeven',
            );
            computedFormName = 'refereeSeven';
          }
        } else {
          const refereeReport = refereeReports.find(
            (report) => report.id === refereeReportId,
          );

          if (!refereeReport) {
            devError(
              `WizardIndependentAssessor: referee report ${refereeReportId} not found in refereeReports array, defaulting computedFormName to refereeSeven`,
            );
            computedFormName = 'refereeSeven';
          } else {
            const { refereeUserId } = refereeReport.attributes;

            if (refereeUserId === nominationReferee.refereeSevenUserId) {
              computedFormName = 'refereeSeven';
            } else if (refereeUserId === nominationReferee.refereeEightUserId) {
              computedFormName = 'refereeEight';
            } else if (refereeUserId === nominationReferee.refereeNineUserId) {
              computedFormName = 'refereeNine';
            } else {
              devError(
                `WizardIndependentAssessor: refereeUserId ${refereeUserId} did not match slots 7/8/9 (${nominationReferee.refereeSevenUserId}/${nominationReferee.refereeEightUserId}/${nominationReferee.refereeNineUserId}), defaulting computedFormName to refereeSeven`,
              );
              computedFormName = 'refereeSeven';
            }
          }
        }

        let refData = {};
        if (
          nominationReferee.refereeSevenTypeId !== null ||
          nominationReferee.refereeEightTypeId !== null ||
          nominationReferee.refereeNineTypeId !== null
        ) {
          refData = await getIndependentAssessorFormData(
            nominationReferee,
            organisations,
            computedFormName,
            titlesTypes,
          );
          setOriginalRefereeData(refData);
          setFormStatus(1);
        }

        const nomination = nominationArray.find(
          (nom) => nom.id === nominationId,
        );

        let sectionalCommitteeName;

        if (
          nomination?.attributes?.electionTypeId ===
          ELECTION_TYPE.CORRESPONDING_MEMBER
        ) {
          sectionalCommitteeName = 'Corresponding membership';
        } else if (
          nomination?.attributes?.electionTypeId ===
          ELECTION_TYPE.SPECIAL_ELECTION
        ) {
          sectionalCommitteeName = 'Special Election';
        } else {
          sectionalCommitteeName =
            sectionalCommittees[nomination.attributes.sectionalCommitteeId];
        }

        const candidateName = candidates[nomination.attributes.candidateId];
        setRefereeReportData({
          candidateName,
          sectionalCommitteeName,
          electionTypeId: nomination.attributes.electionTypeId,
          testEmail: originalUserDetails.email,
        });

        setInitialFormData({
          ...refData,
          existingFellows: existingUsers?.existingFellows || [],
          existingNonAasReferees: existingUsers?.existingNonAasReferees || [],
          conflictRoleFellows: existingUsers?.conflictRoleFellows || {},
        });

        setFormName(computedFormName);
        setIsLoading(false);
      } catch (error) {
        devError('Error setting independent assessor form values:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    userDetails,
    nominationId,
    candidates,
    nominationArray,
    sectionalCommittees,
    organisations,
    refereeReportId,
    refereeReports,
    isRedirecting,
    originalUserDetails,
  ]);

  return {
    formStatus,
    refereeReportData,
    isRedirecting,
    showModal,
    modalBodyText,
    formName,
    notification,
    initialFormData,
    isLoading,
    onSubmit,
    handleCancelSelect,
    handleConfirmSelect,
  };
};

export default useAssessorWizard;
